import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import AIPanel from "@/components/AIPanel";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * The Emergent Auth flow redirects here with `session_id` in the URL fragment
 * (e.g. #session_id=xxx). This page detects it, exchanges it via
 * POST /api/auth/session, then redirects to /dashboard. If arrived without a
 * fragment (normal navigation), it just renders the profile shell.
 */
export default function Profile() {
    const { user, refresh, loading } = useAuth();
    const navigate = useNavigate();
    const [exchanging, setExchanging] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [aiPanelOpen, setAiPanelOpen] = useState(false);

    useEffect(() => {
        // Detect session_id in the URL fragment
        const hash = window.location.hash || "";
        if (!hash) return;
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) return;
        const sessionId = decodeURIComponent(match[1]);

        setExchanging(true);
        (async () => {
            try {
                await api.postSession(sessionId);
                window.localStorage.setItem("glms_session", "1");
                // Clear the fragment so a refresh doesn't retry
                window.history.replaceState(
                    {},
                    document.title,
                    window.location.pathname,
                );
                await refresh();
                toast.success("Signed in with Google");
                navigate("/dashboard", { replace: true });
            } catch (e) {
                toast.error(e.message || "Auth exchange failed");
                setExchanging(false);
            }
        })();
    }, [refresh, navigate]);

    if (exchanging || loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-[var(--color-bg)] text-text-secondary">
                <div className="flex items-center gap-3">
                    <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
                    <span className="text-sm">Completing sign-in…</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen grid place-items-center bg-[var(--color-bg)] text-text-secondary">
                <div className="text-center">
                    <p className="text-sm">Please sign in from the home page.</p>
                    <button
                        onClick={() => navigate("/", { replace: true })}
                        data-testid="profile-home-link"
                        className="mt-4 inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white transition-transform duration-150 hover:scale-[0.98]"
                    >
                        Back to landing
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((c) => !c)}
            />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main
                    data-testid="profile-main"
                    className="flex-1 px-6 md:px-8 py-8 max-w-4xl w-full"
                >
                    <p className="text-[11px] uppercase tracking-widest text-text-secondary">
                        Profile
                    </p>
                    <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
                        Account details
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6"
                    >
                        <div className="flex items-center gap-4">
                            <span className="h-14 w-14 rounded-full bg-[var(--color-panel-ai)] grid place-items-center overflow-hidden">
                                {user.picture ? (
                                    <img
                                        src={user.picture}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <span className="text-base font-semibold">
                                        {(user.name || user.email)
                                            .slice(0, 2)
                                            .toUpperCase()}
                                    </span>
                                )}
                            </span>
                            <div>
                                <p
                                    data-testid="profile-name"
                                    className="text-lg font-semibold"
                                >
                                    {user.name}
                                </p>
                                <p className="text-sm text-text-secondary">
                                    {user.email}
                                </p>
                            </div>
                        </div>

                        <dl className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
                                <dt className="text-[11px] uppercase tracking-widest text-text-secondary">
                                    Role
                                </dt>
                                <dd
                                    data-testid="profile-role"
                                    className="mt-1 text-sm font-medium"
                                >
                                    {user.role}
                                </dd>
                            </div>
                            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
                                <dt className="text-[11px] uppercase tracking-widest text-text-secondary">
                                    Credits
                                </dt>
                                <dd
                                    data-testid="profile-credits"
                                    className="mt-1 text-sm font-medium tabular-nums"
                                >
                                    {user.credits}
                                </dd>
                            </div>
                            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
                                <dt className="text-[11px] uppercase tracking-widest text-text-secondary">
                                    Reputation
                                </dt>
                                <dd
                                    data-testid="profile-reputation"
                                    className="mt-1 text-sm font-medium tabular-nums"
                                >
                                    {user.reputation ?? 0}
                                </dd>
                            </div>
                        </dl>
                    </motion.div>
                </main>
            </div>
            <AIPanel
                open={aiPanelOpen}
                onToggle={() => setAiPanelOpen((v) => !v)}
            />
        </div>
    );
}
